import { useState, useEffect, useCallback, useMemo } from "react";
import { useLocation } from "react-router";
import VideoBox from "../../casinoComponents/videoBox";
import CasinoHeading from "../../casinoComponents/casinoHeading";
import CasinoLastResult from "../../casinoComponents/casinoLastResult";
import BetTableSuper3 from "../../casinoComponents/betTableSuper3";
import SuperOver3CardsOverlay from "../../casinoComponents/videoBox/overlays/SuperOver3CardsOverlay";
import FiveCricketLayout from "../superOver/FiveCricketLayout";
import FiveCricketMyBet from "../superOver/FiveCricketMyBet";
import FiveCricketScorecard from "../superOver/FiveCricketScorecard";
import useFiveCricketScorecard from "../superOver/useFiveCricketScorecard";
import SuperOver3LastResults from "./SuperOver3LastResults";
import { getCasinoGameDetails, getMatchExposure, getCasinoOpenBets } from "../../../apiservices/CasionApi";
import ViewMoreModal from "../../../components/ViewMoreModal";
import { extractCasinoGame } from "../../../utils/casinoGame";
import { superOverSessionBet } from "../../../utils/casinoMyBets";
import "../../../styles/games/superover.css";
import "../../../styles/games/superover3.css";

// Mini SuperOver: one 4-ball over per inning (reference "16-0 (1.0)", "Needed 17 runs from 3 balls"),
// and its one wicket ends it
const OVER_BALLS = 4;
const MAX_WICKETS = 1;
const DEFAULT_NAME = "superover3";

/**
 * casino/superover3 (Mini SuperOver) in the reference five-cricket shell:
 *   .casino-table.five-cricket.super-over.detail-page-container > .game-header + .banner (scorecard)
 *   + video (4-ball overlay) + .market-container, then Last Result after .casino-table;
 *   right sidebar: #my-game-bets + an empty .cricket-rule card (as the reference renders it).
 */
export default function SuperOver3() {
  const [gameData, setGameData] = useState(null);
  const [subData, setSubData] = useState([]);
  const [exposures, setExposures] = useState({});
  const [myBets, setMyBets] = useState([]);
  const [showViewMoreModal, setShowViewMoreModal] = useState(false);

  const { pathname } = useLocation();
  // Routes are matched case-insensitively (casino/superOver3 == casino/superover3); the feed and
  // titles are keyed by the lowercase reference game key.
  const gameName = (extractCasinoGame(pathname) || DEFAULT_NAME).toLowerCase();
  const gameType = gameName;
  const iframeSrc = `https://stream-s-43.uhdmovies.online/casino-stream?id=${gameName}`;
  const matchId =
    gameData?.data?.data?.t1?.gmid?.toString() ||
    gameData?.data?.data?.mid?.toString() ||
    gameData?.data?.mid?.toString() ||
    gameData?.mid?.toString();

  useEffect(() => {
    const name = (extractCasinoGame(pathname) || DEFAULT_NAME).toLowerCase();

    const fetchGameData = async () => {
      try {
        const response = await getCasinoGameDetails(name);
        setGameData(response);
        setSubData(response?.data?.data?.t2 || []);
      } catch (err) {
        console.error("Error fetching game data: ", err);
      }
    };

    fetchGameData();
    const interval = setInterval(fetchGameData, 1500);
    return () => clearInterval(interval);
  }, [pathname]);

  const fetchExposure = useCallback(async () => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const userId = user?.user_id || user?.id || user?.staff_id || user?.owner_id;

      if (userId && matchId) {
        const response = await getMatchExposure(userId, matchId);
        if (response.success && response.data) {
          const exposureMap = {};
          response.data.forEach((item) => {
            const teamName = item.team_name || item.selection || item.player_name || "";
            const exposureAmount = parseFloat(item.exposure_amount) || 0;
            // Fancy session row: one runs-ladder market per row (game_type "superover3:session <row>")
            // — the row shows the panel's worst total.
            const ladderRow = String(item.game_type || "").match(/^[^:]+:session (.+)$/)?.[1].trim();
            if (ladderRow) {
              const worst = Math.min(exposureMap[ladderRow] ?? Infinity, exposureAmount);
              exposureMap[ladderRow] = worst;
              exposureMap[ladderRow.toLowerCase()] = worst;
              return;
            }
            if (teamName) {
              exposureMap[teamName] = exposureAmount;
              exposureMap[teamName.toLowerCase()] = exposureAmount;
              exposureMap[teamName.trim()] = exposureAmount;
            }
          });
          setExposures(exposureMap);
        }
      }
    } catch (error) {
      console.error("Error fetching exposure:", error);
    }
  }, [matchId]);

  const fetchMyBetsData = useCallback(async () => {
    const name = (extractCasinoGame(pathname) || DEFAULT_NAME).toLowerCase();
    if (!name) return;
    try {
      const response = await getCasinoOpenBets(name);
      if (response?.success && Array.isArray(response.data)) {
        const formattedBets = response.data.map((bet) => ({
          // My Bets "Gametype" column (casino_bets.game_name)
          description: bet.description ?? bet.game_name ?? bet.gameName,
          userName: bet.username || bet.userName || "-",
          matchedBet: bet.matchedBet || bet.nation || bet.selection || bet.player_name || "-",
          selection: bet.selection || bet.player_name || bet.matchedBet || "",
          player_name: bet.player_name || "",
          odds: bet.odds ?? bet.userrate ?? "0",
          stake: bet.stake ?? bet.amount ?? "0",
          placeDate: bet.placeDate ?? bet.created_at ?? "-",
          gametype: bet.gametype ?? bet.game_type ?? (bet.type === "lay" ? "Lay" : bet.type === "back" ? "Back" : "-"),
          type: bet.type || null,
          exposer: parseFloat(bet.exposer || bet.exposure_amount || "0") || 0,
        }));
        setMyBets(formattedBets);
      }
    } catch (error) {
      console.error("Error fetching my bets (open-downline):", error);
    }
  }, [pathname]);

  useEffect(() => {
    if (matchId) {
      const t = setTimeout(() => fetchExposure(), 2000);
      const exposureInterval = setInterval(fetchExposure, 2000);
      return () => {
        clearTimeout(t);
        clearInterval(exposureInterval);
      };
    }
  }, [fetchExposure, matchId]);

  useEffect(() => {
    const first = setTimeout(fetchMyBetsData, 0);
    const intervalId = setInterval(fetchMyBetsData, 3000);
    return () => {
      clearTimeout(first);
      clearInterval(intervalId);
    };
  }, [fetchMyBetsData]);

  // Admin panel places no bets: a price click refreshes exposure and My Bets
  const handleBetClick = () => {
    fetchExposure();
    fetchMyBetsData();
  };

  const t1 = gameData?.data?.data?.t1;
  const roundId = t1?.gmid || gameData?.data?.data?.mid || gameData?.data?.mid || gameData?.mid || "";
  const leftTime = t1?.lt ?? gameData?.data?.data?.lt;
  const rawScorecard = useFiveCricketScorecard(gameType, true);
  const scorecard = useMemo(
    () =>
      rawScorecard
        ? {
            ...rawScorecard,
            overBalls: rawScorecard.overBalls ?? OVER_BALLS,
            inningsBalls: rawScorecard.inningsBalls ?? OVER_BALLS,
            maxWickets: rawScorecard.maxWickets ?? MAX_WICKETS,
          }
        : null,
    [rawScorecard],
  );

  return (
    <FiveCricketLayout
      tableClass="five-cricket super-over detail-page-container"
      betCount={myBets.length}
      afterTable={
        <CasinoLastResult
          gameType={gameType}
          resultComponent={<SuperOver3LastResults gameType={gameType} labels={["I", "A"]} roundId={roundId} />}
        />
      }
      rightSide={
        <>
          <FiveCricketMyBet
            bets={myBets.map((b) => { const s = superOverSessionBet(b); return s ? { ...b, selection: s.nation, odds: s.rate } : b; })}
            onViewMore={() => setShowViewMoreModal(true)}
          />{" "}
          <div className="card mb-2 my-bet cricket-rule" />
        </>
      }
    >
      <CasinoHeading name={t1?.ename || ""} roundId={roundId} />{" "}
      <div className="banner">
        {Number(t1?.scard) !== 2 ? (
          <FiveCricketScorecard scorecard={scorecard} gmid={t1?.gmid} scard={t1?.scard} isFiveOver={false} />
        ) : null}
      </div>{" "}
      <div>
        <VideoBox
          className="mt-0"
          iframeSrc={iframeSrc}
          timerValue={leftTime}
          gameType={gameType}
          cardsOverlay={<SuperOver3CardsOverlay card={t1?.card} />}
        />
      </div>{" "}
      <BetTableSuper3 data={subData} onBetClick={handleBetClick} exposures={exposures} myBets={myBets} />
      {t1?.remark ? (
        <div className="casino-remark mt-3">
          <div className="remark-icon">
            <img src={`${import.meta.env.BASE_URL}img/icons/remark.png`} alt="" />
          </div>{" "}
          <marquee>{t1.remark}</marquee>
        </div>
      ) : null}
      <ViewMoreModal
        show={showViewMoreModal}
        onHide={() => setShowViewMoreModal(false)}
        title="View More"
        sectionKey={gameName}
        formatNation={(nat, bet) => superOverSessionBet(bet)?.nation ?? nat}
        formatRate={(bet) => superOverSessionBet(bet)?.rate ?? bet.odds ?? bet.userrate}
      />
    </FiveCricketLayout>
  );
}
